import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
	filter, Subject, takeUntil, tap,
} from 'rxjs';

import useStyles from 'isomorphic-style-loader/useStyles';
import { AuthService, Container, history } from '../../../symphony';

import { StaticPagesContext } from '../../private/contexts';
import {
	SlStaticPagesActionTypes,
	StaticPagesActionsService,
} from '../../private/services/static-pages/sl-static-pages-actions.service';
import { SlStaticPagesDataService } from '../../private/services/static-pages/sl-static-pages-data.service';
import { PreFooter } from '../../private/components/LanguageLandingPage/PreFooter/PreFooter';
import { LanguageLandingPage } from '../../private/global-interfaces';
import { Header } from '../../private/components/LanguageLandingPage/Header/Header';
import { WhyLearn } from '../../private/components/LanguageLandingPage/WhyLearn/WhyLearn';
import { WhatCanIAchieve } from '../../private/components/LanguageLandingPage/WhatCanIAchieve/WhatCanIAchieve';
import { AllCourses } from '../../private/components/LanguageLandingPage/AllCourses/AllCourses';
import { RunCodeYourself } from '../../private/components/LanguageLandingPage/RunCodeYourself/RunCodeYourself';
import { Blog } from '../../private/components/LanguageLandingPage/Blog/Blog';

import s from './SlLanguageLandingPage.scss';
import { ILanguageLandingPageResponse } from '../../../api/public/static-pages-api';
import { SlFooter } from '../../../navigation/public/SlFooter/SlFooter';
import { SlHelmet } from '../../../shared/public/SlHelmet/SlHelmet';
import { useComponentWillMount } from '../../../shared/public/SlUtils/useComponentWillMount';

export function SlLanguageLandingPage() {
	useStyles(s);
	const { languageAlias } = useParams();
	const unsubscriber$ = useRef(new Subject<void>());
	const { actions$, actionsStream } = Container.take(
		StaticPagesContext,
		StaticPagesActionsService,
	);

	useComponentWillMount(() => {
		actionsStream.pipe(takeUntil(unsubscriber$.current)).subscribe();

		actions$.next({
			type: SlStaticPagesActionTypes.getLanguageLandingPageData,
			requestType: 'parallel',
			data: {
				languageAlias,
			},
		});
	});

	const { languageLandingPageData$ } = Container.take(
		StaticPagesContext,
		SlStaticPagesDataService,
	);

	const [languageLandingPageData, setLanguageLandingPageData] = useState<ILanguageLandingPageResponse>(languageLandingPageData$.value);

	useEffect(() => {
		const authService = Container.take('global', AuthService);

		if (authService.isLoggedIn()) {
			history.push('/learn');
		}

		languageLandingPageData$
			.pipe(
				filter((m) => !!m),
				takeUntil(unsubscriber$.current),
				tap(setLanguageLandingPageData),
			)
			.subscribe();

		return () => {
			unsubscriber$.current.next();
			unsubscriber$.current.complete();
			Container.reset(StaticPagesContext);
		};
	}, []);

	const Components = {
		[LanguageLandingPage.Header]: Header,
		[LanguageLandingPage.WhyLearn]: WhyLearn,
		[LanguageLandingPage.WhatCanIAchieve]: WhatCanIAchieve,
		[LanguageLandingPage.AllCourses]: AllCourses,
		[LanguageLandingPage.RunCodeYourself]: RunCodeYourself,
		[LanguageLandingPage.Blog]: Blog,
		[LanguageLandingPage.PreFooter]: PreFooter,
	};

	return (
		<>
			<SlHelmet
				title={languageLandingPageData?.metaTags.title}
				description={languageLandingPageData?.metaTags.description}
				imgUrl={languageLandingPageData?.metaTags.courseImage}
			/>
			<main className="sl-language">
				{languageLandingPageData?.pages.map((section) => {
					const SectionComponent = Components[section.pageName];
					return SectionComponent ? (
						<SectionComponent
							key={section.pageId}
							data={section.data}
						/>
					) : null;
				})}
			</main>
			<SlFooter />
		</>
	);
}
